"use client";

import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { PortableText } from "@portabletext/react";
import { stegaClean } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";

gsap.registerPlugin(ScrollTrigger, SplitText);

export interface TextScrollFadeInProps
	extends React.HTMLAttributes<HTMLHeadingElement> {
	portableText?: any[];
}

const TextScrollFadeIn: React.FC<TextScrollFadeInProps> = ({
	children,
	portableText,
	...props
}) => {
	const wrapperRef = useRef<HTMLHeadingElement | null>(null);

	useEffect(() => {
		if (!wrapperRef.current) return;

		const split = new SplitText(wrapperRef.current, {
			type: "words",
			wordsClass: "word",
		});
		const words = [
			// @ts-ignore
			...wrapperRef.current?.querySelectorAll(".word"),
		] as HTMLElement[];
		// SplitText sets words to inline-block, which stops text-decoration
		// (underline on <em>) from propagating across them. Only opacity is
		// animated here, so plain inline keeps the underline intact.
		words.forEach((word) => {
			word.style.display = "inline";
		});
		const ctx = gsap.context(() => {
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: wrapperRef.current,
					end: "center center",
					scrub: 2,
				},
			});
			tl.from(words, {
				opacity: 0.3,
				stagger: 1.2,
				duration: 2,
				ease: "power1.inOut",
			});
		});

		return () => {
			ctx.revert();
			split.revert();
		};
	}, [children, portableText]);

	return (
		<div {...props} ref={wrapperRef}>
			{portableText ? (
				<PortableText value={stegaClean(portableText)} components={richTextComponent} />
			) : (
				typeof children === "string" ? stegaClean(children) : children
			)}
		</div>
	);
};
TextScrollFadeIn.displayName = "TextScrollFadeIn";

export default TextScrollFadeIn;
